import type { App, Directive } from 'vue'
import { watchEffect } from 'vue'
import { useAccess } from '../composables/useAccessRol'

interface DirectiveCache {
  originalParent: Node | null;
  anchor: Comment | null;
  element: HTMLElement | null;
  stopEffect?: () => void;
}

const elementMap = new WeakMap<HTMLElement, DirectiveCache>()

const accessRolDirective: Directive = {
  mounted(el, binding) {
    const { value, modifiers } = binding

    const { hasAccess } = useAccess({
      section: value.section,
      actions: value?.actions,
    })

    const stopEffect = watchEffect(() => {
      const keepInDOM = Boolean(modifiers.keep)

      if (!hasAccess.value) {
        handleNoAccess(el, keepInDOM)
      } else {
        restoreElement(el, keepInDOM)
      }
    })
    
    const cache: DirectiveCache = {
      originalParent: null,
      anchor: null,
      element: el,
      stopEffect
    }
    elementMap.set(el, cache)
  },
  beforeUnmount(el) {
    const cache = elementMap.get(el)
    if (cache?.stopEffect) {
      cache.stopEffect()
    }
    
    cleanupElement(el)
    elementMap.delete(el)
  }
}

function handleNoAccess(el: HTMLElement, keepDOM: boolean) {
  if (keepDOM) {
    el.style.display = 'none'
  } else {
    if (!elementMap.has(el)) {
      const comment = document.createComment('access-restricted ')
      const parent = el.parentNode
      
      elementMap.set(el, {
        originalParent: parent,
        anchor: comment,
        element: el
      })
      
      parent?.replaceChild(comment, el)
    }
  }
}

function restoreElement(el: HTMLElement, keepDOM: boolean) {
  if (keepDOM) {
    el.style.display = ''
  } else {
    const cache = elementMap.get(el)
    if (cache) {
      cache.originalParent?.replaceChild(el, cache.anchor!)
      elementMap.delete(el)
    }
  }
}

function cleanupElement(el: HTMLElement) {
  const cache = elementMap.get(el)
  if (cache) {
    cache.originalParent?.replaceChild(el, cache.anchor!)
    elementMap.delete(el)
  }
}

export function registerAccessRolDirective(app: App) {
  app.directive('access', accessRolDirective)
}

export { accessRolDirective }
